/* Appsalon: global free-shipping celebration.
   Opens the cart drawer and animates the shipping truck once when the cart crosses
   the free-shipping threshold. This is global for AJAX cart changes, not only quick buy.
*/
(function () {
  'use strict';

  if (window.AppsalonFreeShippingCelebration) return;

  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!nativeFetch) return;

  const state = {
    reached: null,
    checking: false,
    pendingCheck: null,
    lastCelebratedAt: 0
  };

  function getKrownSettings() {
    return window.KROWN && window.KROWN.settings ? window.KROWN.settings : {};
  }

  function getSettings() {
    const settings = getKrownSettings();

    return {
      enabled: settings.open_drawer_on_free_shipping_reached === true ||
        settings.quick_buy_open_drawer_on_free_shipping_reached === true,
      freeShippingEnabled: settings.cart_free_shipping_enabled === true,
      freeShippingThreshold: parseInt(settings.cart_free_shipping_threshold, 10) || 0
    };
  }

  function getFreeShippingThreshold(settings = getSettings()) {
    if (!settings.freeShippingEnabled || !settings.freeShippingThreshold) return 0;

    const currencyRate = window.Shopify && Shopify.currency && Shopify.currency.rate
      ? Number(Shopify.currency.rate)
      : 1;

    return Math.round(settings.freeShippingThreshold * currencyRate);
  }

  function isFreeShippingReached(cart, settings = getSettings()) {
    const threshold = getFreeShippingThreshold(settings);
    if (!threshold || !cart) return false;

    const total = parseInt(cart.total_price, 10) || 0;
    return total >= threshold;
  }

  function getCartJsonUrl() {
    const routes = getKrownSettings().routes || {};
    const cartUrl = routes.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  }

  function getCart() {
    return nativeFetch(getCartJsonUrl(), {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    }).then(response => response.json());
  }

  function toUrl(input) {
    try {
      if (typeof input === 'string') return new URL(input, window.location.origin);
      if (input && typeof input.url === 'string') return new URL(input.url, window.location.origin);
    } catch (error) {
      return null;
    }

    return null;
  }

  function stripJs(pathname) {
    return pathname.replace(/\.js$/, '');
  }

  function isCartMutation(input, init) {
    const url = toUrl(input);
    if (!url) return false;

    const method = (
      (init && init.method) ||
      (input && typeof input.method === 'string' ? input.method : 'GET') ||
      'GET'
    ).toUpperCase();

    if (!['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) return false;

    const routes = getKrownSettings().routes || {};
    const mutationPaths = [
      routes.cart_add_url,
      routes.cart_change_url,
      routes.cart_update_url,
      '/cart/add',
      '/cart/change',
      '/cart/update',
      '/cart/clear'
    ]
      .filter(Boolean)
      .map(path => stripJs(new URL(path, window.location.origin).pathname));

    const requestPath = stripJs(url.pathname);
    return mutationPaths.some(path => requestPath === path || requestPath.endsWith(path));
  }

  function getCartTrigger() {
    const selectors = [
      '.button--cart-handle',
      '.mobile-cart-button',
      '[aria-controls="site-cart-sidebar"]',
      'a[href$="/cart"]',
      'a[href*="/cart?"]'
    ];

    const candidates = Array.from(document.querySelectorAll(selectors.join(',')));

    return candidates.find(element => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom >= 0 &&
        rect.top <= window.innerHeight &&
        style.visibility !== 'hidden' &&
        style.display !== 'none' &&
        style.opacity !== '0'
      );
    }) || candidates[0] || null;
  }

  function pulseCartIcon() {
    const cartTrigger = getCartTrigger();
    if (!cartTrigger) return;

    cartTrigger.classList.remove('appsalon-cart-pulse');
    void cartTrigger.offsetWidth;
    cartTrigger.classList.add('appsalon-cart-pulse');

    window.setTimeout(() => {
      cartTrigger.classList.remove('appsalon-cart-pulse');
    }, 900);
  }

  function getShippingTruckElements() {
    const selectors = [
      '#site-cart-sidebar [data-appsalon-free-shipping-truck]',
      '#site-cart-sidebar .shipping-progress__icon--truck',
      '[data-appsalon-free-shipping-truck]',
      '.shipping-progress__icon--truck'
    ];

    return Array.from(document.querySelectorAll(selectors.join(',')))
      .filter((truck, index, trucks) => trucks.indexOf(truck) === index);
  }

  function animateShippingTruck(truck) {
    if (!truck) return;

    const progress = truck.closest('.shipping-progress');
    if (progress) {
      progress.classList.remove('appsalon-shipping-progress-celebrate');
      void progress.offsetWidth;
      progress.classList.add('appsalon-shipping-progress-celebrate');
    }

    truck.classList.remove('appsalon-shipping-truck-celebrate');
    void truck.offsetWidth;
    truck.classList.add('appsalon-shipping-truck-celebrate');

    window.clearTimeout(truck._appsalonShippingCelebrateTimer);
    truck._appsalonShippingCelebrateTimer = window.setTimeout(() => {
      truck.classList.remove('appsalon-shipping-truck-celebrate');
      if (progress) {
        progress.classList.remove('appsalon-shipping-progress-celebrate');
      }
    }, 1900);
  }

  function highlightFreeShippingTruck() {
    // Cart drawer content is often re-rendered asynchronously after refreshCart().
    // Keep a short celebration window open so both the old and the newly rendered
    // shipping notice can receive the animation.
    const celebrationUntil = Date.now() + 4800;
    window.AppsalonFreeShippingCelebrateUntil = celebrationUntil;

    const run = () => {
      getShippingTruckElements().forEach(animateShippingTruck);
    };

    [0, 120, 300, 550, 850, 1250, 1750, 2400, 3200].forEach(delay => {
      window.setTimeout(run, delay);
    });

    const observeTarget = document.getElementById('site-cart-sidebar') || document.body;

    if (observeTarget && 'MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        if (Date.now() <= celebrationUntil) {
          run();
        }
      });

      observer.observe(observeTarget, {
        childList: true,
        subtree: true
      });

      window.setTimeout(() => {
        observer.disconnect();
      }, 5000);
    }
  }

  function openCartDrawerAndCelebrate() {
    if (document.body.classList.contains('template-cart')) {
      highlightFreeShippingTruck();
      pulseCartIcon();
      return;
    }

    if (typeof window.refreshCart === 'function') {
      window.refreshCart(false);
    }

    const open = () => {
      const cartDrawer = document.getElementById('site-cart-sidebar') || document.querySelector('[data-js-site-cart-sidebar]');

      if (cartDrawer && typeof cartDrawer.show === 'function') {
        cartDrawer.show();
      } else {
        const cartTrigger = getCartTrigger();
        if (cartTrigger) cartTrigger.click();
      }

      pulseCartIcon();
      highlightFreeShippingTruck();
    };

    window.setTimeout(open, 120);
  }

  function celebrate(cart) {
    const now = Date.now();

    // Prevent duplicate celebrations from closely spaced/concurrent cart requests.
    if (now - state.lastCelebratedAt < 2500) return false;

    state.lastCelebratedAt = now;

    document.dispatchEvent(new CustomEvent('appsalon:free-shipping:reached', {
      bubbles: true,
      detail: { cart }
    }));

    openCartDrawerAndCelebrate();
    return true;
  }

  function handleKnownCartChange(previousCart, updatedCart) {
    const settings = getSettings();
    if (!settings.enabled) return false;

    const threshold = getFreeShippingThreshold(settings);
    if (!threshold || !previousCart || !updatedCart) return false;

    const previousReached = isFreeShippingReached(previousCart, settings);
    const updatedReached = isFreeShippingReached(updatedCart, settings);
    const alreadyReached = state.reached === true;

    state.reached = updatedReached;

    if (!alreadyReached && !previousReached && updatedReached) {
      return celebrate(updatedCart);
    }

    return false;
  }

  function refreshReachedState(cart) {
    const settings = getSettings();
    state.reached = isFreeShippingReached(cart, settings);
    return state.reached;
  }

  function checkCartAfterMutation(beforeReachedPromise) {
    if (state.checking) {
      window.clearTimeout(state.pendingCheck);
      state.pendingCheck = window.setTimeout(() => checkCartAfterMutation(beforeReachedPromise), 180);
      return;
    }

    state.checking = true;

    Promise.resolve(beforeReachedPromise)
      .catch(() => state.reached)
      .then(beforeReached => {
        return getCart().then(cart => {
          const settings = getSettings();
          const afterReached = isFreeShippingReached(cart, settings);
          const wasReached = state.reached !== null ? state.reached : beforeReached;

          state.reached = afterReached;

          if (!wasReached && afterReached && settings.enabled) {
            celebrate(cart);
          }
        });
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => {
        state.checking = false;
      });
  }

  function captureReachedBeforeMutation() {
    if (state.reached !== null) return Promise.resolve(state.reached);

    return getCart()
      .then(cart => refreshReachedState(cart))
      .catch(() => state.reached);
  }

  function scheduleCheckAfterMutation(beforeReachedPromise) {
    window.clearTimeout(state.pendingCheck);
    state.pendingCheck = window.setTimeout(() => {
      checkCartAfterMutation(beforeReachedPromise);
    }, 220);
  }

  window.fetch = function appsalonFreeShippingFetch(input, init) {
    const shouldWatch = isCartMutation(input, init);
    const shouldWaitForInitialState = shouldWatch && state.reached === null;
    const beforeReachedPromise = shouldWatch ? captureReachedBeforeMutation() : null;

    const mutationRequest = shouldWaitForInitialState
      ? Promise.resolve(beforeReachedPromise).catch(() => null).then(() => nativeFetch(input, init))
      : nativeFetch(input, init);

    return mutationRequest.then(response => {
      if (shouldWatch && response && response.ok) {
        scheduleCheckAfterMutation(beforeReachedPromise);
      }

      return response;
    });
  };

  // Initialize state so stores that already have free shipping do not celebrate on the next add.
  getCart()
    .then(cart => refreshReachedState(cart))
    .catch(() => {
      state.reached = null;
    });

  window.AppsalonFreeShippingCelebration = {
    getCart,
    getFreeShippingThreshold,
    isFreeShippingReached,
    handleKnownCartChange,
    highlightFreeShippingTruck,
    openCartDrawerAndCelebrate,
    refreshReachedState,
    state
  };
})();
